"use client";

import type {
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";

import { useImageDescriptions } from "../../../hooks/use-image-descriptions";

import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  ArrowRight,
  ClipboardList,
  Download,
  Eye,
  FileText,
  Import,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useDebouncedSave } from "../../../hooks/use-debounced-save";

import { useDraftAutosave } from "../../../hooks/use-draft-autosave";

import {
  useVersionAwareFiles,
  useVersionAwareSiteData,
  useVersionAwareWorks,
} from "../../../hooks/use-version-aware-data";

import { useNotification } from "../../../hooks/useNotification";

import { useAppDispatch } from "../../../redux/hooks";

import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";

import {
  fetchSiteDataById,
  updateSiteData,
} from "../../../redux/slices/siteDataSlice.js";

import {
  createWork,
  deleteWork,
  fetchWorks,
  updateWork,
} from "../../../redux/slices/workSlice.js";

import { punchListHSEManagementProgress } from "../../../utils/componentUtils.js";

import { notificationHelpers } from "../../../utils/notificationHelpers";

import { OfflineImage } from "../../shared/offline-image";

import { Badge } from "../../ui/badge";

import { Button } from "../../ui/button.jsx";

import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card.jsx";

import { Label } from "../../ui/label.jsx";

import { LoadingIndicator } from "../../ui/loading-indicator";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";

import {
  OptimizedCheckbox,
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from "../optimized-form-field";

import { ImageCapturePicker } from "../shared/image-capture-picker";

import { LocalSectionImportControl } from "../shared/local-section-import-control";

// ===== INTERFACES =====

interface FileAttachment extends AuditFileMetadata {
  id: string;

  fileName: string;

  fileUrl: string;

  fileType: string;

  fileSize: number;

  uploadedDate: string;

  description?: string;

  originalName?: string;

  fileDescription?: string;

  uploadDate?: string;
}

interface HSEManagementData {
  inductionProcess: boolean;

  visitorSignInBookChecked: boolean;

  securityMeasures: string;

  recommendations: string;

  safetyPlan: string;

  trainingCompleted: string;

  inspectionDate: string;

  inspectionNotes: string;
}

interface WorkItem {
  id: string;

  _id?: string;

  type?: "incident" | "document";

  incidentType?: string;

  severity?: string;

  documentName?: string;

  version?: string;

  status?: string;

  date?: string;

  description?: string;

  actionsTaken?: string;

  reportedBy?: string;

  affectedPersons?: string;

  investigationStatus?: string;

  comments?: string;

  workCategory?: string;
}

// ===== CONSTANTS & HELPERS =====

const initialData: HSEManagementData = {
  inductionProcess: false,

  visitorSignInBookChecked: false,

  securityMeasures: "",

  recommendations: "",

  safetyPlan: "",

  trainingCompleted: "",

  inspectionDate: "",

  inspectionNotes: "",
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;

  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  );
};

// ===== MAIN COMPONENT =====

export const HSEManagement = memo(function HSEManagement({
  siteId,

  templateId,

  reportId,

  setSessionProgress,

  readOnly = false,

  previousCompletedReports = [],
}: {
  siteId: string;

  templateId: string | null;

  reportId?: string | null;

  sessionProgress: SessionProgress;

  setSessionProgress: SetSessionProgress;

  readOnly?: boolean;

  isViewingHistoricalVersion?: boolean;

  versionData?: any;

  previousCompletedReports?: any[];
}) {
  const { notify } = useNotification();

  // ===== STATE =====

  const [formData, setFormData] = useState<HSEManagementData>(initialData);

  const [activityData, setActivityData] = useState<
    Record<string, Partial<WorkItem>>
  >({});

  const [selectedFile, setSelectedFile] = useState<FileAttachment | null>(null);

  const [uploadingWorkImages, setUploadingWorkImages] = useState<
    Record<string, boolean>
  >({});

  const [workProgressData, setWorkProgressData] = useState<WorkItem[]>([]);

  const [activeTab, setActiveTab] = useState<"incidents" | "documents">(
    "incidents",
  );

  // Previous incidents state

  const [, setPreviousSiteData] = useState<{
    siteData: any;

    works: any[];
  } | null>(null);

  const [, setIsLoadingPreviousData] = useState(false);

  // Search state

  const [, setIsSearchOpen] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // ===== REDUX =====

  const files = useVersionAwareFiles(readOnly);

  const [imageDescriptions, setImageDescriptions] = useImageDescriptions(files);

  const dispatch = useAppDispatch();

  const currentSiteData = useVersionAwareSiteData(readOnly);

  const currentWorksData = useVersionAwareWorks(readOnly);

  // Autosave unsaved edits to IndexedDB so nothing is lost if the app closes

  // before the debounced server save fires (e.g. offline, tab closed, phone

  // loses power).

  const draftKey =
    !readOnly && reportId ? `report:${reportId}:hse-management` : null;

  const { restoreDraft, clearSavedDraft } = useDraftAutosave(
    draftKey,
    formData,
  );

  const draftRestoredRef = useRef(false);

  // Restore any unsaved draft once on mount, before server data can

  // overwrite it below.

  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;

    draftRestoredRef.current = true;

    restoreDraft().then((draft) => {
      if (draft?.data) {
        setFormData((prev) => ({ ...prev, ...draft.data }));
      }
    });
  }, [draftKey, restoreDraft]);

  // Once local form state matches the confirmed server record, the

  // IndexedDB draft is redundant — clear it so a future mount doesn't

  // restore stale local data over a fresher server record.

  useEffect(() => {
    if (!draftKey || !currentSiteData) return;

    if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
      clearSavedDraft();
    }
  }, [draftKey, currentSiteData, formData, clearSavedDraft]);

  // ===== EFFECTS =====

  useEffect(() => {
    dispatch(fetchWorks({ siteId, templateId, reportId }) as any);

    dispatch(fetchFiles({ siteId, reportId }) as any);
  }, [dispatch, siteId, templateId, reportId]);

  // Close search on click outside

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close search on Escape

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);

        searchInputRef.current?.blur();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const incidents = workProgressData.filter(
      (w: any) => w.workCategory === "hse-incident",
    );

    const documents = workProgressData.filter(
      (w: any) => w.workCategory === "hse-document-review",
    );

    const progress = punchListHSEManagementProgress(
      incidents,

      documents,

      formData,

      files,
    );

    setSessionProgress((prev) => ({
      ...prev,

      [templateId || "punch-list"]: {
        ...(prev[templateId || "punch-list"] || {}),

        "hse-management": progress,
      },
    }));
  }, [formData, workProgressData, templateId, files, setSessionProgress]);

  // ===== FETCH PREVIOUS DATA =====

  const fetchPreviousSiteData = useCallback(
    async (previousSiteId: string) => {
      setIsLoadingPreviousData(true);

      try {
        const siteDataResponse = await dispatch(
          fetchSiteDataById(previousSiteId),
        ).unwrap();

        const worksResponse = await dispatch(
          fetchWorks({ siteId: previousSiteId, templateId }),
        ).unwrap();

        setPreviousSiteData({
          siteData: siteDataResponse,

          works: worksResponse || [],
        });
      } catch (error) {
        console.error("Failed to fetch previous site data:", error);
      } finally {
        setIsLoadingPreviousData(false);
      }
    },
    [dispatch, templateId],
  );

  // Fetch previous site data when available

  useEffect(() => {
    if (currentSiteData?.previousSiteId) {
      fetchPreviousSiteData(currentSiteData.previousSiteId);
    }
  }, [currentSiteData?.previousSiteId, fetchPreviousSiteData]);

  // ===== SAVE HANDLERS =====

  const handleSaveWork = useCallback(
    async (data: typeof activityData) => {
      try {
        const importedWorkEntries = workProgressData

          .filter((work: any) => work?._sourceId && !work?._id)

          .map((work: any) => [work.id, work] as [string, Partial<WorkItem>]);

        const workEntries = [
          ...Object.entries(data),

          ...importedWorkEntries.filter(([workId]) => !data[workId]),
        ];

        const mutationResult1 = await Promise.all(
          workEntries.map(([workId, pendingWorkData]) => {
            const existingWork = workProgressData.find(
              (work: any) => work?._id === workId || work?.id === workId,
            );

            const { _id, ...existingWorkData } = existingWork || {};

            const isImportedWork =
              String(workId).startsWith("imported-") || !existingWork?._id;

            const payload = {
              ...(existingWorkData || {}),

              ...pendingWorkData,

              siteId,

              templateId,
            };

            return isImportedWork
              ? requireMutationSuccess(dispatch(createWork(payload as any)))
              : dispatch(updateWork({ workId, workData: payload })).unwrap();
          }),
        );

        const config = notificationHelpers.formSaveSuccess("Work Data", [
          mutationResult1,
        ]);

        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Work Data",
          errorMessage(error),
        );

        notify(config.message, config.type, config.options);

        throw error;
      }
    },

    [dispatch, notify, workProgressData, siteId, templateId],
  );

  const handleSaveForm = useCallback(
    async (data: typeof formData) => {
      if (!siteId) return;

      try {
        const mutationResult2 = await requireMutationSuccess(
          dispatch(
            updateSiteData({
              siteDataId: siteId,

              siteDataInfo: {
                reportId,
                punchList: {
                  hseSummary: {
                    inductionProcess: data.inductionProcess || false,

                    visitorSignInBookChecked:
                      data.visitorSignInBookChecked || false,

                    securityMeasures: data.securityMeasures || "",

                    recommendations: data.recommendations || "",

                    safetyPlan: data.safetyPlan || "",

                    trainingCompleted: data.trainingCompleted || "",

                    inspectionDate: data.inspectionDate || "",

                    inspectionNotes: data.inspectionNotes || "",
                  },
                },
              },
            } as any),
          ),
        );

        const config = notificationHelpers.formSaveSuccess("Form Data", [
          mutationResult2,
        ]);

        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Form Data",
          errorMessage(error),
        );

        notify(config.message, config.type, config.options);

        throw error;
      }
    },

    [dispatch, siteId, reportId, notify],
  );

  const handleSaveImages = useCallback(
    async (descriptions: Record<string, string>) => {
      try {
        const mutationResult3 = await Promise.all(
          Object.entries(descriptions).map(([fileId, fileDescription]) =>
            requireMutationSuccess(
              dispatch(updateFile({ fileId, fileData: { fileDescription } })),
            ),
          ),
        );

        const config = notificationHelpers.formSaveSuccess(
          "Image Descriptions",
          [mutationResult3],
        );

        notify(config.message, config.type, config.options);
      } catch (error) {
        const config = notificationHelpers.formSaveError(
          "Image Descriptions",
          errorMessage(error),
        );

        notify(config.message, config.type, config.options);

        throw error;
      }
    },

    [dispatch, notify],
  );

  // ===== DEBOUNCED SAVE HOOKS =====

  const {
    isSaving: isWorkSaving,

    lastSaved: lastSavedWork,

    hasUnsavedChanges: hasWorkUnsavedChanges,

    saveError: workSaveError,

    forceSave: forceWorkSave,
  } = useDebouncedSave(activityData, {
    delay: 5000,

    onSave: handleSaveWork,

    onError: (error) => console.error("Work save error:", error),

    readOnly,
  });

  const {
    resetBaseline: formResetBaseLine,

    isSaving: isFormSaving,

    lastSaved: lastSavedForm,

    hasUnsavedChanges: hasFormUnsavedChanges,

    saveError: formSaveError,

    forceSave: forceFormSave,
  } = useDebouncedSave(formData, {
    delay: 5000,

    onSave: handleSaveForm,

    onError: (error) => console.error("Form save error:", error),

    readOnly,
  });

  useEffect(() => {
    const hseSummary = currentSiteData?.punchList?.hseSummary || {};

    const newFormData = { ...initialData, ...hseSummary };

    formResetBaseLine(newFormData);

    setFormData(newFormData);

    const hseWorks =
      currentWorksData?.filter(
        (w: any) =>
          w.workCategory === "hse-incident" ||
          w.workCategory === "hse-document-review",
      ) || [];

    setWorkProgressData(hseWorks);
  }, [currentSiteData, currentWorksData, templateId, formResetBaseLine]);

  const {
    isSaving: isImageSaving,

    lastSaved: lastSavedImage,

    hasUnsavedChanges: hasImageUnsavedChanges,

    saveError: imageSaveError,

    forceSave: forceImageSave,
  } = useDebouncedSave(imageDescriptions, {
    delay: 5000,

    onSave: handleSaveImages,

    onError: (error) => console.error("Image description save error:", error),

    readOnly,
  });

  const isSaving = isFormSaving || isWorkSaving || isImageSaving;

  const hasUnsavedChanges =
    hasFormUnsavedChanges || hasWorkUnsavedChanges || hasImageUnsavedChanges;

  const saveError = formSaveError || workSaveError || imageSaveError;

  const lastSaved = useMemo(() => {
    const dates = [lastSavedForm, lastSavedImage, lastSavedWork].filter(
      Boolean,
    );

    if (dates.length === 0) return null;

    return new Date(Math.max(...dates.map((date) => new Date(date).getTime())));
  }, [lastSavedForm, lastSavedWork, lastSavedImage]);

  const forceSave = useCallback(() => {
    forceFormSave();

    forceWorkSave();

    forceImageSave();
  }, [forceFormSave, forceWorkSave, forceImageSave]);

  // ===== FILE OPERATIONS =====

  const handleFileUpload = (
    files: FileList | File[] | null,

    filterData: any,

    entityId?: string,
  ) => {
    if (!files) return;

    const uploadKey = JSON.stringify(filterData);

    setUploadingWorkImages((prev) => ({ ...prev, [uploadKey]: true }));

    const fileArray = Array.from(files);

    requireMutationSuccess(
      dispatch(
        uploadMultipleFiles({
          files: fileArray,

          metadata: {
            siteId: siteId,

            entityType: `hse-management`,

            entityId: entityId || siteId,

            reportId,
          },

          // filterData,
        }),
      ),
    )
      .then((mutationOutcome) => {
        fileArray.forEach((file) => {
          const config = notificationHelpers.imageUploadSuccess(
            file.name,
            mutationOutcome,
          );

          notify(config.message, config.type, config.options);
        });
      })

      .catch((error) => {
        const config = notificationHelpers.imageUploadError(
          fileArray.length === 1
            ? fileArray[0].name
            : `${fileArray.length} files`,

          errorMessage(error),
        );

        notify(config.message, config.type, config.options);
      })

      .finally(() => {
        setUploadingWorkImages((prev) => ({ ...prev, [uploadKey]: false }));
      });
  };

  const removeFile = (fileId: string) => {
    requireMutationSuccess(dispatch(deleteFile(fileId) as any))
      .then((mutationOutcome) => {
        const config = notificationHelpers.imageDeleteSuccess(
          "Image",
          mutationOutcome,
        );

        notify(config.message, config.type, config.options);
      })

      .catch((error) => {
        const config = notificationHelpers.imageDeleteError(
          "Image",
          errorMessage(error),
        );

        notify(config.message, config.type, config.options);
      });
  };

  const updateImageDescription = useCallback(
    (fileId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [fileId]: description }));
    },

    [setImageDescriptions],
  );

  // ===== WORK OPERATIONS =====

  const addIncident = async () => {
    try {
      await requireMutationSuccess(
        dispatch(
          createWork({
            siteId,

            templateId,

            type: "incident",

            incidentType: "near-miss",

            severity: "low",

            date: new Date().toISOString().split("T")[0],

            workCategory: "hse-incident",

            workType: "incident",

            status: "pending",

            description: "",
          } as any),
        ),
      )
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionCreate(
            "Incident Report",
            mutationOutcome,
          );

          notify(config.message, config.type, config.options);
        })

        .catch((error) => {
          const config = notificationHelpers.actionCreateError(
            "Incident Report",

            errorMessage(error),
          );

          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionCreateError(
        "Incident Report",

        errorMessage(error),
      );

      notify(config.message, config.type, config.options);
    }
  };

  const addDocumentReview = async () => {
    try {
      await requireMutationSuccess(
        dispatch(
          createWork({
            siteId,

            templateId,

            type: "document",

            documentName: "",

            version: "1.0",

            status: "pending",

            date: new Date().toISOString().split("T")[0],

            workCategory: "hse-document-review",

            workType: "document",
          } as any),
        ),
      )
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionCreate(
            "Document Review",
            mutationOutcome,
          );

          notify(config.message, config.type, config.options);
        })

        .catch((error) => {
          const config = notificationHelpers.actionCreateError(
            "Document Review",

            errorMessage(error),
          );

          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionCreateError(
        "Document Review",

        errorMessage(error),
      );

      notify(config.message, config.type, config.options);
    }
  };

  const removeActivity = async (id: string) => {
    try {
      await requireMutationSuccess(dispatch(deleteWork(id) as any))
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionDelete(
            "HSE Item",
            mutationOutcome,
          );

          notify(config.message, config.type, config.options);
        })

        .catch((error) => {
          const config = notificationHelpers.actionDeleteError(
            "HSE Item",

            errorMessage(error),
          );

          notify(config.message, config.type, config.options);
        });
    } catch (error) {
      const config = notificationHelpers.actionDeleteError(
        "HSE Item",

        errorMessage(error),
      );

      notify(config.message, config.type, config.options);
    }
  };

  const updateActivity = (
    id: string,

    field: keyof WorkItem | string,

    value: any,
  ) => {
    setWorkProgressData((prev) =>
      prev.map((work) =>
        work.id === id || work._id === id ? { ...work, [field]: value } : work,
      ),
    );

    setActivityData((prev) => ({
      ...prev,

      [id]: { ...prev[id], [field]: value },
    }));
  };

  const getWorkValue = <K extends keyof WorkItem>(
    item: WorkItem,

    field: K,
  ): WorkItem[K] =>
    field in (activityData[item.id] || {})
      ? (activityData[item.id][field] as WorkItem[K])
      : item[field];

  // ===== PREVIOUS INCIDENTS & SEARCH =====

  // Check if incident is already imported

  // Filter previous incidents based on search query

  // Import single incident

  // ===== HELPER FUNCTIONS =====

  const isUploadingForSection = (filterData: any) => {
    const uploadKey = JSON.stringify(filterData);

    return uploadingWorkImages[uploadKey] || false;
  };

  const getLoadingStatus = () => {
    if (saveError) return "error";

    if (isSaving) return "saving";

    if (hasUnsavedChanges) return "unsaved";

    if (lastSaved) return "saved";

    return "idle";
  };

  const getFilesByEntity = (filterData: any, entityId?: string) => {
    return files.filter(
      (file: any) =>
        file?.relatedTo?.entityType === `hse-management` &&
        (!entityId || file?.relatedTo?.entityId === entityId),

      //  &&

      // file?.relatedTo?.filterData &&

      // JSON.stringify(file?.relatedTo?.filterData) ===

      //   JSON.stringify(filterData),
    );
  };

  // ===== RENDER FUNCTIONS =====

  const renderFileAttachments = (filterData: any, entityId?: string) => {
    const entityFiles = getFilesByEntity(filterData, entityId);

    const isUploading = isUploadingForSection(filterData);

    return (
      <div className="space-y-3">
        {isUploading && (
          <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-card dark:border-border">
            <div className="text-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />

              <p className="text-sm text-slate-600 dark:text-muted-foreground">
                Uploading images...
              </p>
            </div>
          </div>
        )}

        <Label className="text-sm font-medium flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Supporting Documents <span className="text-red-500">*</span>
        </Label>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {entityFiles.map((file: any) => (
            <div key={file.id} className="relative group">
              {file?.fileType?.startsWith("image/") ? (
                <div className="aspect-video bg-slate-100 dark:bg-card rounded overflow-hidden">
                  <OfflineImage
                    fileId={file.id}

                    src={file.fileUrl}

                    alt={file.originalName}

                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-slate-100 dark:bg-card rounded flex items-center justify-center">
                  <FileText className="w-8 h-8 text-slate-400 dark:text-muted-foreground" />
                </div>
              )}

              <div className="absolute top-1 right-1 flex gap-1">
                <Button
                  type="button"

                  variant="secondary"

                  size="sm"

                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"

                  onClick={() => setSelectedFile(file)}
                >
                  <Eye className="w-3 h-3" />
                </Button>

                {!readOnly && (
                  <Button
                    type="button"

                    variant="destructive"

                    size="sm"

                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"

                    onClick={() => removeFile(file.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>

              <div className="mt-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-slate-700 dark:text-muted-foreground truncate">
                    {file.originalName}
                  </div>

                  <Badge variant="outline" className="text-xs">
                    {formatFileSize(file.fileSize)}
                  </Badge>
                </div>

                {!readOnly && (
                  <input
                    type="text"

                    placeholder="Add description..."

                    value={
                      imageDescriptions[file.id] || file.fileDescription || ""
                    }

                    onChange={(e) =>
                      updateImageDescription(file.id, e.target.value)
                    }

                    className="w-full px-2 py-1 mt-1 text-xs border border-slate-200 dark:border-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-muted dark:text-foreground dark:focus:ring-ring"
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {!readOnly && (
          <ImageCapturePicker
            buttonText="Click to upload photos"

            isUploading={isUploading}

            className="py-3 border-2 border-dashed border-slate-300 dark:border-border hover:border-slate-400 dark:hover:border-primary-border"

            onConfirm={(selectedFiles) =>
              handleFileUpload(selectedFiles, filterData, entityId)
            }
          />
        )}
      </div>
    );
  };

  // ===== FILTERED DATA =====

  const incidents = workProgressData.filter(
    (w: any) => w.workCategory === "hse-incident",
  );

  const documents = workProgressData.filter(
    (w: any) => w.workCategory === "hse-document-review",
  );

  // ===== RENDER =====

  return (
    <Card className="w-full">
      {!readOnly && (
        <LocalSectionImportControl
          previousCompletedReports={previousCompletedReports}

          sectionName="hse-management"

          siteId={siteId}

          reportId={reportId || undefined}

          readOnly={readOnly}

          setFormData={setFormData}

          workProgressData={workProgressData}

          setWorkProgressData={setWorkProgressData}
        />
      )}

      {/* Floating Save Button - Only when not read-only */}

      {!readOnly && (
        <div className="fixed top-25 right-3 z-40 sm:right-4">
          <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
            {/* Status indicator - same size as save button */}

            {false && (
              <div
                className="

          bg-white/95 backdrop-blur-sm

          rounded-full border-2 border-gray-200/80

          shadow-lg

          w-12 h-12 sm:w-16 sm:h-16

          flex items-center justify-center

          group relative

          transition-all duration-300 ease-out

          transform hover:scale-110

          cursor-default

          p-2

        "
              >
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                    Saved
                  </span>

                  <div className="flex items-center justify-center h-6">
                    <LoadingIndicator
                      status={getLoadingStatus()}

                      lastSaved={lastSaved}

                      error={saveError}
                    />
                  </div>
                </div>

                {/* Tooltip - Fixed with pointer-events-none */}

                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {getLoadingStatus() === "saving"
                    ? "Saving..."
                    : getLoadingStatus() === "saved"
                      ? `Saved ${lastSaved?.toLocaleTimeString()}`
                      : getLoadingStatus() === "error"
                        ? "Save failed"
                        : "Ready to save"}
                </div>

                {/* Error indicator */}

                {getLoadingStatus() === "error" && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>

                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}

            {/* Save button - Fixed with pointer-events-none */}

            <Button
              onClick={forceSave}

              disabled={isSaving || readOnly}

              className={`

          relative rounded-full w-12 h-12 sm:w-16 sm:h-16

          transition-all duration-300 ease-out

          flex flex-col items-center justify-center

          backdrop-blur-sm border-2

          shadow-lg hover:shadow-xl

          transform hover:scale-110 active:scale-95

          group

          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }

          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}

        `}
            >
              {isSaving ? (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>

                  <span className="text-[10px] leading-none">Saving</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save className="w-5 h-5 sm:mb-1" />

                  <span className="hidden text-[10px] font-medium leading-none sm:block">
                    Save
                  </span>
                </div>
              )}

              {/* Unsaved changes indicator */}

              {hasUnsavedChanges && !isSaving && (
                <div className="absolute -top-1 -right-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>

                  <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                </div>
              )}

              {/* Tooltip - Fixed with pointer-events-none */}

              <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                {hasUnsavedChanges ? "Save changes" : "No changes to save"}
              </div>
            </Button>
          </div>
        </div>
      )}

      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>HSE Management</CardTitle>

          <div className="flex gap-2 flex-wrap items-center">
            {/*{

              !readOnly && (

                <div className="relative" ref={searchRef}>

                   <div className="relative">

                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />

                    <input

                      ref={searchInputRef}

                      type="text"

                      placeholder="Search previous incidents..."

                      value={searchQuery}

                      onChange={(e) => {

                        setSearchQuery(e.target.value);

                        setIsSearchOpen(true);

                      }}

                      onFocus={() => setIsSearchOpen(true)}

                      className="pl-9 pr-4 py-2 w-64 text-sm border border-slate-300 dark:border-border rounded-lg bg-white dark:bg-card focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-foreground"

                    />

                    {searchQuery && (

                      <button

                        onClick={() => {

                          setSearchQuery("");

                          searchInputRef.current?.focus();

                        }}

                        className="absolute right-3 top-1/2 transform -translate-y-1/2"

                      >

                        <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />

                      </button>

                    )}

                    {isLoadingPreviousData && (

                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">

                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />

                      </div>

                    )}

                  </div>



                   {isSearchOpen && (

                    <div className="absolute top-full mt-2 w-[500px] max-h-[500px] overflow-y-auto bg-white dark:bg-card border border-slate-200 dark:border-border rounded-lg shadow-2xl z-50">

                      <div className="p-3 border-b border-slate-200 dark:border-border bg-slate-50 dark:bg-surface rounded-t-lg">

                        <div className="flex items-center justify-between">

                          <div className="flex items-center gap-2">

                            <History className="w-4 h-4 text-blue-500" />

                            <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">

                              Previous Visit Incidents

                            </span>

                          </div>

                          <Badge variant="secondary" className="text-xs">

                            {filteredPreviousIncidents.length} available

                          </Badge>

                        </div>

                      </div>



                      {filteredPreviousIncidents.length === 0 ? (

                        <div className="p-8 text-center">

                          <ClipboardList className="w-12 h-12 text-slate-300 dark:text-muted-foreground mx-auto mb-3" />

                          <p className="text-sm text-slate-500 dark:text-muted-foreground">

                            {searchQuery

                              ? "No incidents match your search"

                              : "All previous incidents have been imported"}

                          </p>

                        </div>

                      ) : (

                        <div className="p-2 space-y-2">

                          {filteredPreviousIncidents.map((incident: any) => {

                            const incidentId = incident.id || incident._id;

                            const isImporting =

                              importingIncidentId === incidentId;



                            return (

                              <div

                                key={incidentId}

                                className="p-3 rounded-lg border border-slate-200 dark:border-border hover:border-blue-300 dark:hover:border-primary-border hover:bg-blue-50/50 dark:hover:bg-brand/20 transition-all group"

                              >

                                <div className="flex items-start justify-between gap-3">

                                  <div className="flex-1 min-w-0">

                                    <div className="flex items-center gap-2 mb-2 flex-wrap">

                                      {incident.severity && (

                                        <Badge

                                          variant={

                                            incident.severity === "critical"

                                              ? "destructive"

                                              : incident.severity === "high"

                                                ? "outline"

                                                : "secondary"

                                          }

                                          className="text-xs"

                                        >

                                          {incident.severity}

                                        </Badge>

                                      )}

                                      {incident.incidentType && (

                                        <Badge

                                          variant="secondary"

                                          className="text-xs"

                                        >

                                          {incident.incidentType}

                                        </Badge>

                                      )}

                                      <span className="text-xs text-slate-400">

                                        {incident.date || "No date"}

                                      </span>

                                    </div>



                                    <p className="text-sm text-slate-700 dark:text-muted-foreground mb-2">

                                      {highlightMatch(

                                        incident.description ||

                                          "No description",

                                        searchQuery,

                                      )}

                                    </p>



                                    {incident.actionsTaken && (

                                      <div className="mb-2">

                                        <span className="text-xs font-medium text-slate-500 dark:text-muted-foreground">

                                          Actions:

                                        </span>

                                        <p className="text-xs text-slate-600 dark:text-muted-foreground mt-0.5">

                                          {highlightMatch(

                                            incident.actionsTaken,

                                            searchQuery,

                                          )}

                                        </p>

                                      </div>

                                    )}



                                    <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">

                                      {incident.reportedBy && (

                                        <span>By: {incident.reportedBy}</span>

                                      )}

                                      {incident.investigationStatus && (

                                        <Badge

                                          variant="outline"

                                          className="text-xs"

                                        >

                                          {incident.investigationStatus}

                                        </Badge>

                                      )}

                                    </div>

                                  </div>



                                  <Button

                                    size="sm"

                                    onClick={(e) => {

                                      e.stopPropagation();

                                      importSingleIncident(incident);

                                    }}

                                    disabled={isImporting}

                                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity gap-1"

                                  >

                                    {isImporting ? (

                                      <Loader2 className="w-3 h-3 animate-spin" />

                                    ) : (

                                      <Import className="w-3 h-3" />

                                    )}

                                    {isImporting ? "Importing..." : "Import"}

                                  </Button>

                                </div>

                              </div>

                            );

                          })}

                        </div>

                      )}

                    </div>

                  )}

                </div>

              )

            }*/}

            {/* Existing Buttons */}

            {!readOnly && (
              <>
                <Button size="sm" onClick={addIncident} disabled={readOnly}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Incident
                </Button>

                <Button
                  size="sm"

                  onClick={addDocumentReview}

                  disabled={readOnly}

                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Document Review
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Main Tabs */}

        <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="incidents">
              Incidents
              {incidents.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {incidents.length}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger value="documents">
              Document Reviews
              {documents.length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {documents.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="incidents">
            <div className="pt-6 space-y-4">
              {incidents.length > 0 ? (
                <div className="space-y-3">
                  {incidents.map((work: any) => (
                    <Card key={work.id || work._id} className="overflow-hidden">
                      <CardContent className="pt-4 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {work.severity && (
                                <Badge
                                  variant={
                                    work.severity === "critical"
                                      ? "destructive"
                                      : work.severity === "high"
                                        ? "outline"
                                        : "secondary"
                                  }
                                >
                                  {work.severity}
                                </Badge>
                              )}

                              {work.incidentType && (
                                <Badge variant="secondary">
                                  {work.incidentType}
                                </Badge>
                              )}

                              {work.comments?.includes(
                                "Imported from previous visit",
                              ) && (
                                <Badge
                                  variant="outline"

                                  className="text-xs border-blue-300 text-blue-600 dark:border-primary-border dark:text-link"
                                >
                                  <Import className="w-3 h-3 mr-1" />
                                  Imported
                                </Badge>
                              )}
                            </div>
                          </div>

                          {!readOnly && (
                            <Button
                              size="sm"

                              variant="destructive"

                              onClick={() =>
                                removeActivity(work.id || work._id)
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <OptimizedSelect
                            label="Incident Type"

                            value={
                              getWorkValue(work, "incidentType") || "near-miss"
                            }

                            onChange={(value) =>
                              updateActivity(
                                work.id || work._id,

                                "incidentType",

                                value,
                              )
                            }

                            options={[
                              { value: "near-miss", label: "Near Miss" },

                              { value: "injury", label: "Injury" },

                              {
                                value: "environmental",

                                label: "Environmental",
                              },

                              { value: "other", label: "Other" },
                            ]}

                            readOnly={readOnly}

                            required
                          />

                          <OptimizedSelect
                            label="Severity"

                            value={getWorkValue(work, "severity") || "low"}

                            onChange={(value) =>
                              updateActivity(
                                work.id || work._id,

                                "severity",

                                value,
                              )
                            }

                            options={[
                              { value: "low", label: "Low" },

                              { value: "medium", label: "Medium" },

                              { value: "high", label: "High" },

                              { value: "critical", label: "Critical" },
                            ]}

                            readOnly={readOnly}

                            required
                          />

                          <OptimizedInput
                            label="Reported By"

                            value={getWorkValue(work, "reportedBy") || ""}

                            onChange={(e) =>
                              updateActivity(
                                work.id || work._id,

                                "reportedBy",

                                e,
                              )
                            }

                            placeholder="Reporter name"

                            readOnly={readOnly}

                            required
                          />

                          <OptimizedInput
                            label="Date"

                            type="date"

                            value={getWorkValue(work, "date") || ""}

                            onChange={(e) =>
                              updateActivity(work.id || work._id, "date", e)
                            }

                            readOnly={readOnly}

                            required
                          />
                        </div>

                        <OptimizedTextarea
                          label="Description"

                          value={getWorkValue(work, "description") || ""}

                          onChange={(e) =>
                            updateActivity(
                              work.id || work._id,

                              "description",

                              e,
                            )
                          }

                          placeholder="Describe the incident"

                          readOnly={readOnly}

                          rows={2}

                          required
                        />

                        <OptimizedTextarea
                          label="Actions Taken"

                          value={getWorkValue(work, "actionsTaken") || ""}

                          onChange={(e) =>
                            updateActivity(
                              work.id || work._id,

                              "actionsTaken",

                              e,
                            )
                          }

                          placeholder="Describe corrective actions"

                          readOnly={readOnly}

                          rows={2}

                          required
                        />

                        <OptimizedSelect
                          label="Investigation Status"

                          value={
                            getWorkValue(work, "investigationStatus") ||
                            "pending"
                          }

                          onChange={(value) =>
                            updateActivity(
                              work.id || work._id,

                              "investigationStatus",

                              value,
                            )
                          }

                          options={[
                            { value: "pending", label: "Pending" },

                            { value: "in-progress", label: "In Progress" },

                            { value: "completed", label: "Completed" },
                          ]}

                          readOnly={readOnly}

                          required
                        />

                        {/* Import comment display */}

                        {work.comments?.includes(
                          "Imported from previous visit",
                        ) && (
                          <div className="bg-blue-50 dark:bg-brand/40 border border-blue-200 dark:border-primary-border rounded-md p-2">
                            <div className="flex items-center gap-2">
                              <ArrowRight className="w-3 h-3 text-blue-500" />

                              <p className="text-xs text-blue-600 dark:text-link">
                                {work.comments}
                              </p>
                            </div>
                          </div>
                        )}

                        {renderFileAttachments(
                          { [work.id || work._id]: true },

                          work.id || work._id,
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-muted-foreground text-sm">
                  <ClipboardList className="w-12 h-12 text-slate-300 dark:text-muted-foreground mx-auto mb-3" />

                  <p>No incidents recorded yet.</p>

                  {currentSiteData?.previousSiteId && !readOnly && (
                    <p className="mt-2 text-xs">
                      Use the search bar above to find and import incidents from
                      previous visits.
                    </p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="documents">
            <div className="pt-6 space-y-4">
              {documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map((work: any) => (
                    <Card key={work.id || work._id} className="overflow-hidden">
                      <CardContent className="pt-4 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">
                                {getWorkValue(work, "documentName") ||
                                  "Unnamed Document"}
                              </span>

                              {work.status && (
                                <Badge
                                  variant={
                                    work.status === "approved"
                                      ? "default"
                                      : work.status === "rejected"
                                        ? "destructive"
                                        : "secondary"
                                  }
                                >
                                  {work.status}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {!readOnly && (
                            <Button
                              size="sm"

                              variant="destructive"

                              onClick={() =>
                                removeActivity(work.id || work._id)
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <OptimizedInput
                            label="Document Name"

                            value={getWorkValue(work, "documentName") || ""}

                            onChange={(e) =>
                              updateActivity(
                                work.id || work._id,

                                "documentName",

                                e,
                              )
                            }

                            placeholder="Document name"

                            readOnly={readOnly}

                            required
                          />

                          <OptimizedInput
                            label="Version"

                            value={getWorkValue(work, "version") || ""}

                            onChange={(e) =>
                              updateActivity(work.id || work._id, "version", e)
                            }

                            placeholder="e.g., 1.0"

                            readOnly={readOnly}

                            required
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <OptimizedSelect
                            label="Status"

                            value={getWorkValue(work, "status") || "pending"}

                            onChange={(value) =>
                              updateActivity(
                                work.id || work._id,

                                "status",

                                value,
                              )
                            }

                            options={[
                              { value: "pending", label: "Pending" },

                              { value: "approved", label: "Approved" },

                              {
                                value: "needs-revision",

                                label: "Needs Revision",
                              },

                              { value: "rejected", label: "Rejected" },
                            ]}

                            readOnly={readOnly}

                            required
                          />

                          <OptimizedInput
                            label="Review Date"

                            type="date"

                            value={getWorkValue(work, "date") || ""}

                            onChange={(e) =>
                              updateActivity(work.id || work._id, "date", e)
                            }

                            readOnly={readOnly}

                            required
                          />

                          <OptimizedTextarea
                            label="Comments"

                            value={getWorkValue(work, "comments") || ""}

                            onChange={(e) =>
                              updateActivity(work.id || work._id, "comments", e)
                            }

                            placeholder="Review comments"

                            readOnly={readOnly}

                            rows={2}

                            required
                          />
                        </div>

                        {renderFileAttachments(
                          { [work.id || work._id]: true },

                          work.id || work._id,
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-muted-foreground text-sm">
                  No document reviews recorded yet.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* HSE Summary Section */}

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">HSE Summary</h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <OptimizedCheckbox
              label="Induction Process Completed"

              id="induction"

              checked={formData.inductionProcess || false}

              onChange={(checked) =>
                setFormData({ ...formData, inductionProcess: !!checked })
              }

              disabled={readOnly}

              required
            />

            <OptimizedCheckbox
              label="Visitor Sign-in Book Checked"

              id="visitor"

              checked={formData.visitorSignInBookChecked || false}

              onChange={(checked) =>
                setFormData({
                  ...formData,

                  visitorSignInBookChecked: !!checked,
                })
              }

              disabled={readOnly}

              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm font-medium">Inspection Date</Label>

              <OptimizedInput
                type="date"

                value={formData.inspectionDate || ""}

                onChange={(e) =>
                  setFormData({ ...formData, inspectionDate: e })
                }

                readOnly={readOnly}
              />
            </div>

            <div>
              <Label className="text-sm font-medium">Training Completed</Label>

              <OptimizedInput
                value={formData.trainingCompleted || ""}

                onChange={(e) =>
                  setFormData({ ...formData, trainingCompleted: e })
                }

                placeholder="e.g., OSHA, First Aid"

                readOnly={readOnly}
              />
            </div>
          </div>

          <div className="space-y-4">
            <OptimizedTextarea
              label="Security Measures"

              value={formData.securityMeasures || ""}

              onChange={(e) =>
                setFormData({ ...formData, securityMeasures: e })
              }

              placeholder="Describe security measures in place"

              readOnly={readOnly}

              required
            />

            <OptimizedTextarea
              label="Safety Plan"

              value={formData.safetyPlan || ""}

              onChange={(e) => setFormData({ ...formData, safetyPlan: e })}

              placeholder="Describe the safety plan"

              readOnly={readOnly}

              required
            />

            <OptimizedTextarea
              label="Inspection Notes"

              value={formData.inspectionNotes || ""}

              onChange={(e) => setFormData({ ...formData, inspectionNotes: e })}

              placeholder="Add inspection notes"

              readOnly={readOnly}
            />

            <OptimizedTextarea
              label="Recommendations"

              value={formData.recommendations || ""}

              onChange={(e) => setFormData({ ...formData, recommendations: e })}

              placeholder="Add recommendations"

              readOnly={readOnly}
            />
          </div>
        </div>
      </CardContent>

      {/* File Preview Modal */}

      {selectedFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {selectedFile.originalName || selectedFile.fileName}
              </h3>

              <Button
                variant="ghost"

                size="sm"

                onClick={() => setSelectedFile(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-4">
              {selectedFile?.fileType?.startsWith("image/") ? (
                <OfflineImage
                  fileId={selectedFile.id}

                  src={selectedFile?.fileUrl}

                  alt={selectedFile?.originalName || selectedFile?.fileName}

                  className="max-w-full max-h-96 mx-auto rounded"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-slate-400 mx-auto mb-4 dark:text-muted-foreground" />

                  <p className="text-slate-600 dark:text-muted-foreground">
                    Preview not available for this file type
                  </p>

                  <Button
                    variant="outline"

                    className="mt-4"

                    onClick={() => window.open(selectedFile.fileUrl, "_blank")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download File
                  </Button>
                </div>
              )}

              {selectedFile.fileDescription && (
                <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                  <p className="text-sm text-slate-700 dark:text-muted-foreground">
                    {selectedFile.fileDescription}
                  </p>
                </div>
              )}

              <div className="mt-4 text-sm text-slate-500 dark:text-muted-foreground space-y-1">
                <p>Size: {formatFileSize(selectedFile.fileSize)}</p>

                <p>
                  Uploaded:{" "}
                  {new Date(
                    selectedFile.uploadedDate || selectedFile.uploadDate,
                  ).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
});
